import { Controller, useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { AddEditProps, User, Warehouse } from '../models/table-models';
import { useTranslation } from 'react-i18next';
import { useAddWarehouse, useUpdateWarehouse } from '../utils/warehouses-hooks';
import CommonInputText from '../components/CommonInputText';
import { useAsyncResource } from 'use-async-resource';
import { useGetAllMainGroups } from '../utils/groups-hooks';
import Select, { SingleValue } from 'react-select';
import { stringsToOptions } from '../components/FormMultiSelect';
import React, { useEffect, useState } from 'react';
import { MainGroup } from '../models/user-models';
import { useGetUsersInGroups } from '../utils/users-hooks';
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";

type WarehouseForm = Omit<Warehouse, 'type'> & {
    isMainWarehouse?: boolean;
    isScrapWarehouse?: boolean;
    isRepairWarehouse?: boolean;
};

export const AddEditWarehouse = ({
    itemToEdit,
    setShowModal,
    refetchEntitiesHook,
}: AddEditProps<Warehouse>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const { register, handleSubmit, formState, control, resetField, getValues, setValue } =
        useForm<WarehouseForm>({
            mode: 'all',
            defaultValues: {
                isMainWarehouse: !itemToEdit?.type,
                isScrapWarehouse: itemToEdit?.type === 1,
                isRepairWarehouse: itemToEdit?.type === 2,
            },
        });
    const [getAllMainGroups] = useAsyncResource(useGetAllMainGroups, []);
    const mainGroup = itemToEdit?.mainGroup;
    const defaultMainGroup = getAllMainGroups().find((item) => item.mainGroup === mainGroup) ?? {
        mainGroup: '',
        subGroups: [],
    };
    const [subGroups, setSubgroups] = useState<string[]>(defaultMainGroup.subGroups);
    const [getAllUsers, fetchAllUsers] = useAsyncResource(useGetUsersInGroups);
    const updateMainGroup = (
        newValue: SingleValue<MainGroup>,
        onFieldChange: (arg0?: string) => void
    ) => {
        onFieldChange(newValue?.mainGroup);
        newValue?.subGroups && setSubgroups(newValue?.subGroups);
        resetField('subGroups', { defaultValue: [] });
        // updateUsers();
    };

    useEffect(() => {
        if (itemToEdit?.mainGroup && itemToEdit?.subGroups) {
            setSubgroups(itemToEdit.subGroups);
            fetchAllUsers(itemToEdit.mainGroup, itemToEdit.subGroups);
        }
    }, []);

    useEffect(
        (allUsers?: User[]) => {
            const formUsers = getValues('users');
            if (allUsers && formUsers && formUsers.length > 0) {
                const filteredUsers = formUsers?.filter((name) =>
                    allUsers?.some((user) => user.username == name)
                );
                resetField('users', { defaultValue: filteredUsers });
            }
        },
        [getAllUsers()]
    );

    const updateUsers = () => {
        const formMainGroup = getValues('mainGroup') || itemToEdit?.mainGroup;
        const formSubgroups = getValues('subGroups') || itemToEdit?.subGroups;
        if (formMainGroup && formSubgroups && formSubgroups.length > 0) {
            fetchAllUsers(formMainGroup, formSubgroups);
        } else if (itemToEdit?.mainGroup && itemToEdit.subGroups)
            fetchAllUsers(itemToEdit.mainGroup, itemToEdit.subGroups);
        else fetchAllUsers('', ['']);
    };

    const mapFormToWarehouse = (formValue: WarehouseForm): Warehouse => {
        const warehouseFields = { ...formValue };
        const isScrapWarehouse = warehouseFields.isScrapWarehouse;
        const isRepairWarehouse = warehouseFields.isRepairWarehouse;

        delete warehouseFields.isMainWarehouse;
        delete warehouseFields.isScrapWarehouse;
        delete warehouseFields.isRepairWarehouse;

        return {
            ...(warehouseFields as Warehouse),
            ...(isScrapWarehouse ? { type: 1 } : isRepairWarehouse ? { type: 2 } : {}),
        };
    };

    const setWarehouseType = (type: 'main' | 'scrap' | 'repair') => {
        setValue('isMainWarehouse', type === 'main', { shouldDirty: true });
        setValue('isScrapWarehouse', type === 'scrap', { shouldDirty: true });
        setValue('isRepairWarehouse', type === 'repair', { shouldDirty: true });
    };

    const useSubmitHandlerAdd = async (formValue: WarehouseForm) => {
        await useAddWarehouse(mapFormToWarehouse(formValue));
        refetchEntitiesHook();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('warehouseSuccess')]} />, toastOptionsTopRight);
    };

    const useSubmitHandlerEdit = async (formValue: WarehouseForm) => {
        await useUpdateWarehouse(mapFormToWarehouse(formValue), itemToEdit?.id);
        refetchEntitiesHook();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('warehouseUpdated')]} />, toastOptionsTopRight);
    };

    return (
        <Form
            id="form"
            className="d-flex flex-column align-items-center p-4  w-100"
            noValidate
            onSubmit={
                itemToEdit ? handleSubmit(useSubmitHandlerEdit) : handleSubmit(useSubmitHandlerAdd)
            }
        >
            <Form.Group
                className=" d-flex flex-column mb-0 align-items-start w-100  mb-3"
                controlId="warehouseName"
            >
                <CommonInputText
                    id="warehouseName"
                    label={t('warehouseName')}
                    defaultValue={itemToEdit?.warehouseName}
                    labelTextColor="text-black"
                    type="text"
                    fieldError={formState.errors.warehouseName}
                    hookRegister={register('warehouseName', {
                        required: {
                            value: true,
                            message: t('warehouseName'),
                        },
                    })}
                />
            </Form.Group>

            <Form.Group
                className="d-flex flex-column mb-3 align-items-start w-100"
                controlId="warehouseType"
            >
                <Form.Label>{t('warehouseType')}</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                    <Controller
                        control={control}
                        name="isMainWarehouse"
                        render={({ field }) => (
                            <Form.Check
                                className="d-flex align-items-center gap-1 mb-0"
                                type="checkbox"
                                id="isMainWarehouse"
                                label={t('warehouseTypeMain')}
                                checked={!!field.value}
                                onChange={() => setWarehouseType('main')}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="isScrapWarehouse"
                        render={({ field }) => (
                            <Form.Check
                                className="d-flex align-items-center gap-1 mb-0"
                                type="checkbox"
                                id="isScrapWarehouse"
                                label={t('warehouseTypeScrap')}
                                checked={!!field.value}
                                onChange={() => setWarehouseType('scrap')}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="isRepairWarehouse"
                        render={({ field }) => (
                            <Form.Check
                                className="d-flex align-items-center gap-1 mb-0"
                                type="checkbox"
                                id="isRepairWarehouse"
                                label={t('warehouseTypeRepair')}
                                checked={!!field.value}
                                onChange={() => setWarehouseType('repair')}
                            />
                        )}
                    />
                </div>
            </Form.Group>

            <div className="d-flex w-100 justify-content-between">
                <Form.Group
                    className="d-flex w-50 flex-column mb-2  align-items-start"
                    controlId="mainGroup"
                >
                    <Form.Label>{t('mainGroup')}</Form.Label>
                    <Controller
                        control={control}
                        name="mainGroup"
                        rules={{ required: true }}
                        defaultValue={defaultMainGroup.mainGroup}
                        render={({ field }) => (
                            <>
                                <Select
                                    className="w-100"
                                    defaultValue={defaultMainGroup}
                                    onChange={(newValue) => updateMainGroup(newValue, field.onChange)}
                                    isDisabled={!!itemToEdit}
                                    options={getAllMainGroups()}
                                    getOptionLabel={({ mainGroup }) => mainGroup}
                                    getOptionValue={({ mainGroup }) => mainGroup}
                                />
                                {formState.errors.mainGroup && (
                                    <p className={`input-error`}>{'*'+t('choose')}</p>
                                )}
                            </>
                        )}
                    />
                </Form.Group>
                <Form.Group
                    className=" d-flex w-50 ms-3 flex-column mb-2 align-items-start "
                    controlId="subGroups"
                >
                    <Form.Label>{t('subGroups')}</Form.Label>
                    <Controller
                        control={control}
                        name="subGroups"
                        defaultValue={itemToEdit?.subGroups}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <>
                                <Select
                                    className="w-100"
                                    isMulti
                                    isDisabled={!!itemToEdit}
                                    value={stringsToOptions(field.value)}
                                    onChange={(newValue) => {
                                        field.onChange(newValue.map(({ value }) => value));
                                        // updateUsers();
                                    }}
                                    options={stringsToOptions(subGroups)}
                                    getOptionLabel={({ name }) => name}
                                    getOptionValue={({ value }) => value}
                                />
                                {formState.errors.subGroups && (
                                    <p className={`input-error`}>{'*'+t('choose')}</p>
                                )}
                            </>
                        )}
                    />
                </Form.Group>
            </div>

            {/*<Form.Group*/}
            {/*    className=" d-flex flex-column mb-0 align-items-start w-100 "*/}
            {/*    controlId="users"*/}
            {/*>*/}
            {/*    <Form.Label>{t('users')}</Form.Label>*/}
            {/*    <Controller*/}
            {/*        control={control}*/}
            {/*        name="users"*/}
            {/*        defaultValue={itemToEdit?.users}*/}
            {/*        render={({ field }) => (*/}
            {/*            <Select*/}
            {/*                className="w-100"*/}
            {/*                isMulti*/}
            {/*                value={stringsToOptions(field.value)}*/}
            {/*                onChange={(newValue) => {*/}
            {/*                    field.onChange(newValue.map(({ value }) => value));*/}
            {/*                }}*/}
            {/*                options={*/}
            {/*                    getAllUsers()?.map((value) => ({*/}
            {/*                        name: value.username,*/}
            {/*                        value: value.username,*/}
            {/*                    })) ?? []*/}
            {/*                }*/}
            {/*                getOptionLabel={({ name }) => name}*/}
            {/*                getOptionValue={({ value }) => value}*/}
            {/*            />*/}
            {/*        )}*/}
            {/*    />*/}
            {/*</Form.Group>*/}
        </Form>
    );
};
